import { AlertCircle, ChevronLeft, ChevronRight, Loader2, Star, User } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axiosHandler';

const Reviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAllReviews, setShowAllReviews] = useState(false);
    const [expandedReviews, setExpandedReviews] = useState(new Set());
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const limit = 20;

    // Minimum swipe distance (in px) to trigger navigation
    const minSwipeDistance = 50;

    // Navigation functions - must be defined before useEffect
    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, [reviews.length]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length);
    }, [reviews.length]);

    const toggleExpandReview = (reviewId) => {
        setExpandedReviews(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reviewId)) {
                newSet.delete(reviewId);
            } else {
                newSet.add(reviewId);
            }
            return newSet;
        });
    };

    // Touch handlers for swipe gestures
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }
    };

    const getCardPosition = (index) => {
        const totalReviews = Math.min(reviews.length, 10);
        let diff = (index - currentIndex + totalReviews) % totalReviews;

        // Normalize diff to be between -totalReviews/2 and totalReviews/2
        if (diff > totalReviews / 2) {
            diff -= totalReviews;
        }

        if (diff === 0) return 'center';
        if (diff === 1) return 'right';
        if (diff === -1) return 'left';
        return 'hidden';
    };

    useEffect(() => {
        fetchReviews();
    }, [page]);

    // Auto-play carousel
    useEffect(() => {
        if (!showAllReviews && reviews.length > 0) {
            const interval = setInterval(() => {
                nextSlide();
            }, 5000); // Change slide every 5 seconds

            return () => clearInterval(interval);
        }
    }, [currentIndex, showAllReviews, reviews.length, nextSlide]);

    const fetchReviews = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get('/api/reviews', {
                params: {
                    page,
                    limit,
                    orderBy: 'rating',
                    order: 'desc',
                    startDate: '2000-01-01'
                },
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            setReviews(response.data.data);
            setMeta(response.data.meta);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            if (err.response?.status === 403) {
                setError('Reviews are not available for this site.');
            } else {
                setError('Failed to load reviews. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating) => {
        return (
            <div className="flex items-center gap-0.5 sm:gap-1">
                {[...Array(5)].map((_, index) => (
                    <Star
                        key={index}
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        style={{
                            fill: index < rating ? '#fdd41f' : 'none',
                            color: index < rating ? '#fdd41f' : '#d1d5db'
                        }}
                    />
                ))}
            </div>
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading && reviews.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading reviews...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Reviews</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 md:py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-9xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 md:mb-16">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                        <span className="text-gray-900">Student </span>
                        <span style={{ color: '#fdd41f' }}>Reviews</span>
                    </h1>
                    {meta?.summary && (
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-base sm:text-xl mt-4 md:mt-6">
                            <div className="flex items-center gap-2">
                                {renderStars(Math.round(meta.summary.averageRating))}
                                <span className="font-bold text-gray-900 ml-2 text-sm sm:text-base">
                                    {meta.summary.averageRating.toFixed(1)} out of 5
                                </span>
                            </div>
                            <span className="text-gray-500 text-sm sm:text-base">
                                ({meta.summary.totalReviews}+ reviews)
                            </span>
                        </div>
                    )}
                </div>

                {/* Carousel Section */}
                {!showAllReviews ? (
                    <div className="relative mb-8 md:mb-16">
                        {/* Carousel Container - All Devices */}
                        <div
                            className="relative h-[450px] sm:h-[500px] md:h-[600px] overflow-hidden touch-pan-y"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <div className="relative h-full flex items-center justify-center">
                                {reviews.slice(0, 10).map((review, index) => {
                                    const position = getCardPosition(index);

                                    const positionStyles = {
                                        center: 'z-20 scale-100 opacity-100 translate-x-0',
                                        left: 'z-10 scale-75 sm:scale-80 opacity-30 sm:opacity-50 -translate-x-[280px] sm:-translate-x-[400px] md:-translate-x-[520px]',
                                        right: 'z-10 scale-75 sm:scale-80 opacity-30 sm:opacity-50 translate-x-[280px] sm:translate-x-[400px] md:translate-x-[520px]',
                                        hidden: 'z-0 scale-50 opacity-0 translate-x-0 pointer-events-none'
                                    };

                                    return (
                                        <div
                                            key={review.id}
                                            className={`absolute transition-all duration-700 ease-in-out ${positionStyles[position] || positionStyles.hidden}`}
                                            style={{
                                                width: '90vw',
                                                maxWidth: '480px',
                                            }}
                                        >
                                            <div
                                                className="bg-white rounded-xl md:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 h-full"
                                                style={{
                                                    borderWidth: '2px',
                                                    borderColor: position === 'center' ? '#fdd41f' : '#e5e7eb'
                                                }}
                                            >
                                                {/* Author Info */}
                                                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                                                    {review.authorPhotoUrl ? (
                                                        <img
                                                            src={review.authorPhotoUrl}
                                                            alt={review.authorName}
                                                            className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover ring-2 sm:ring-4"
                                                            style={{ backgroundColor: '#fef3c7', borderColor: '#fdd41f' }}
                                                            referrerPolicy="no-referrer"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center ring-2 sm:ring-4" style={{ backgroundColor: '#fef3c7', borderColor: '#fdd41f' }}>
                                                            <User className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" style={{ color: '#7c3aed' }} />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-bold text-base sm:text-lg md:text-xl text-gray-900 truncate">{review.authorName}</h3>
                                                        <p className="text-xs sm:text-sm text-gray-500">{formatDate(review.reviewDate)}</p>
                                                    </div>
                                                </div>

                                                {/* Rating */}
                                                <div className="mb-4 sm:mb-6">
                                                    {renderStars(review.rating)}
                                                </div>

                                                {/* Review Text */}
                                                {review.text && (
                                                    <div className="mb-4 sm:mb-6">
                                                        <p
                                                            className={`text-gray-700 leading-relaxed text-sm sm:text-base ${expandedReviews.has(review.id) ? '' : 'line-clamp-4'
                                                                } overflow-hidden transition-all duration-300`}
                                                        >
                                                            {review.text}
                                                        </p>
                                                        {review.text.length > 200 && (
                                                            <button
                                                                onClick={() => toggleExpandReview(review.id)}
                                                                className="text-xs sm:text-sm font-medium mt-2 transition-colors hover:underline"
                                                                style={{ color: '#7c3aed' }}
                                                            >
                                                                {expandedReviews.has(review.id) ? 'Show less' : 'Read more'}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Source Badge */}

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Navigation Arrows */}
                            {window.innerWidth > 768 && (
                                <button
                                    onClick={prevSlide}
                                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-3 md:p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                                    style={{ backgroundColor: '#7c3aed' }}
                                    aria-label="Previous review"
                                >
                                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                                </button>
                            )}
                            {window.innerWidth > 768 && (
                                <button
                                    onClick={nextSlide}
                                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 p-2 sm:p-3 md:p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
                                    style={{ backgroundColor: '#7c3aed' }}
                                    aria-label="Next review"
                                >
                                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                                </button>
                            )}
                        </div>

                        {/* Dots Indicator - All Devices */}
                        {window.innerWidth > 768 && (
                            <div className="flex justify-center gap-1 sm:gap-1.5 md:gap-2 mt-6 sm:mt-8 px-4">
                                {reviews.slice(0, 10).map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentIndex(index)}
                                        className="transition-all duration-300 rounded-full"
                                        style={{
                                            width: index === currentIndex ? '16px' : '8px',
                                            height: '6px',
                                            backgroundColor: index === currentIndex ? '#7c3aed' : '#d1d5db'
                                        }}
                                        aria-label={`Go to review ${index + 1}`}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Show All Button */}
                        <div className="text-center mt-8 sm:mt-12">
                            <button
                                onClick={() => setShowAllReviews(true)}
                                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base text-white font-semibold rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95"
                                style={{ backgroundColor: '#7c3aed' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
                            >
                                Show All Reviews
                                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Grid View - All Reviews */
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
                            {reviews.map((review) => (
                                <div
                                    key={review.id}
                                    className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                                >
                                    {/* Author Info */}
                                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                                        {review.authorPhotoUrl ? (
                                            <img
                                                src={review.authorPhotoUrl}
                                                alt={review.authorName}
                                                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover ring-2 sm:ring-4"
                                                style={{ backgroundColor: '#fef3c7', borderColor: '#fdd41f' }}
                                                referrerPolicy="no-referrer"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center ring-2 sm:ring-4" style={{ backgroundColor: '#fef3c7', borderColor: '#fdd41f' }}>
                                                <User className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" style={{ color: '#7c3aed' }} />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-base sm:text-lg text-gray-900 truncate">{review.authorName}</h3>
                                            <p className="text-xs sm:text-sm text-gray-500">{formatDate(review.reviewDate)}</p>
                                        </div>
                                    </div>

                                    {/* Rating */}
                                    <div className="mb-3 sm:mb-4">
                                        {renderStars(review.rating)}
                                    </div>

                                    {/* Review Text */}
                                    {review.text && (
                                        <div className="mb-4 sm:mb-6">
                                            <p
                                                className={`text-gray-600 leading-relaxed text-sm sm:text-base ${expandedReviews.has(review.id) ? '' : 'line-clamp-4'
                                                    } overflow-hidden transition-all duration-300`}
                                            >
                                                {review.text}
                                            </p>
                                            {review.text.length > 200 && (
                                                <button
                                                    onClick={() => toggleExpandReview(review.id)}
                                                    className="text-xs sm:text-sm font-medium mt-2 transition-colors hover:underline"
                                                    style={{ color: '#7c3aed' }}
                                                >
                                                    {expandedReviews.has(review.id) ? 'Show less' : 'Read more'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                </div>
                            ))}
                        </div>

                        {/* Show Less Button */}
                        <div className="text-center mb-8 sm:mb-12">
                            <button
                                onClick={() => {
                                    setShowAllReviews(false);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-full transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95"
                            >
                                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                Show Less
                            </button>
                        </div>
                    </>
                )}

                {/* Pagination (only show in grid view) */}
                {showAllReviews && meta && meta.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-6 sm:mt-8 px-4">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={!meta.hasPrev || loading}
                            className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Previous</span>
                            <span className="sm:hidden">Prev</span>
                        </button>

                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 text-sm sm:text-base">
                                Page <span className="font-semibold text-gray-900">{meta.page}</span> of{' '}
                                <span className="font-semibold text-gray-900">{meta.totalPages}</span>
                            </span>
                        </div>

                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={!meta.hasNext || loading}
                            className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Loading Overlay for Pagination */}
                {loading && reviews.length > 0 && (
                    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 shadow-xl">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                        </div>
                    </div>
                )}
            </div>

            {/* CSS for hiding scrollbar */}
            <style jsx>{`
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </div>
    );
};

export default Reviews;

